/**
 * Categories, subcategories and tags (FR-CAT-01 to FR-CAT-10).
 *
 * Two levels, both user-managed, in one collection linked by `parentId` (SCHEMA §3.3).
 * Income heads live here too, separated by `kind`, so one screen manages both.
 *
 * Pure: tree shape, depth rules and merge validity need no database.
 */

export type CategoryKind = 'expense' | 'income';

export interface Category {
  id: string;
  name: string;
  parentId: string | null;
  kind: CategoryKind;
  archived: boolean;
  sortOrder: number;
  transactionCount?: number;
}

export interface CategoryNode {
  category: Category;
  children: Category[];
}

export class CategoryError extends Error {}

/** `parentId: null` is a top-level category; anything else is a subcategory of it. */
export function isTopLevel(category: Category): boolean {
  return category.parentId === null;
}

/**
 * Build the two-level tree for one kind.
 *
 * Subcategories whose parent is missing or archived-away are surfaced under a synthetic
 * parent rather than dropped — silently hiding a category that still has transactions
 * against it would make those transactions unreachable from this screen.
 */
export function buildTree(
  categories: Category[],
  kind: CategoryKind,
  { includeArchived = false } = {},
): CategoryNode[] {
  const ofKind = categories.filter(
    (c) => c.kind === kind && (includeArchived || !c.archived),
  );
  const parents = ofKind.filter(isTopLevel).sort(byOrder);
  const known = new Set(parents.map((p) => p.id));

  const nodes = parents.map((category) => ({
    category,
    children: ofKind
      .filter((c) => c.parentId === category.id)
      .sort(byOrder),
  }));

  const orphans = ofKind.filter((c) => c.parentId !== null && !known.has(c.parentId));
  if (orphans.length > 0) {
    nodes.push({
      category: {
        id: '__orphans__',
        name: 'Without a parent',
        parentId: null,
        kind,
        archived: false,
        sortOrder: Number.MAX_SAFE_INTEGER,
      },
      children: orphans.sort(byOrder),
    });
  }

  return nodes;
}

function byOrder(a: Category, b: Category): number {
  return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
}

/**
 * A third level is not permitted (SCHEMA §3.3).
 *
 * Enforced on write rather than trusted: nothing in the data model prevents a chain, and
 * every report groups by exactly two levels.
 */
export function validateParent(
  categories: Category[],
  parentId: string | null,
): void {
  if (parentId === null) return;
  const parent = categories.find((c) => c.id === parentId);
  if (!parent) throw new CategoryError('That parent category does not exist.');
  if (!isTopLevel(parent)) {
    throw new CategoryError(
      `"${parent.name}" is already a subcategory. Categories are two levels deep.`,
    );
  }
}

/**
 * Whether `source` may be merged into `target` (FR-CAT-04).
 *
 * Merging re-points every transaction from source to target, archives source, and
 * forces a rollup recompute — rollup buckets are keyed by category id.
 */
export function validateMerge(
  categories: Category[],
  sourceId: string,
  targetId: string,
): void {
  if (sourceId === targetId) throw new CategoryError('Cannot merge a category into itself.');

  const source = categories.find((c) => c.id === sourceId);
  const target = categories.find((c) => c.id === targetId);
  if (!source || !target) throw new CategoryError('Both categories must exist.');

  if (source.kind !== target.kind) {
    throw new CategoryError(
      'An income head and an expense category cannot be merged — they mean different things.',
    );
  }

  // Merging a parent into its own child would leave that child parented to itself.
  if (target.parentId === source.id) {
    throw new CategoryError(
      `"${target.name}" sits under "${source.name}". Move it out before merging.`,
    );
  }

  if (isTopLevel(source) && !isTopLevel(target)) {
    throw new CategoryError(
      'A top-level category cannot merge into a subcategory; its own children would be orphaned.',
    );
  }
}

/** Subcategories that would be orphaned by archiving a parent. */
export function childrenOf(categories: Category[], parentId: string): Category[] {
  return categories.filter((c) => c.parentId === parentId);
}

export function slugifyName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

/**
 * Near-duplicate detection for the merge-candidate list (FR-CAT-06).
 *
 * The source has Grocery/Groceries, School Fee/School Fees, Car Washing/Car Wash and
 * "Housewarming" against "House warming " with a trailing space. Normalising case,
 * spacing and a trailing "s" catches all of those. The user decides each; nothing
 * auto-merges.
 */
export function normaliseForComparison(name: string): string {
  let s = name.toLowerCase().replace(/[^a-z0-9]+/g, '');

  // Trim a gerund first — "Car Washing" against "Car Wash" is one of the named cases.
  if (s.length > 6 && s.endsWith('ing')) s = s.slice(0, -3);

  // Then reduce a plural to its stem. Stripping a trailing "s" alone is not enough:
  // "groceries" becomes "grocerie", which still does not meet "grocery". Peeling s,
  // then e, then turning a trailing i back into y lands both on the same stem.
  if (s.length > 1 && s.endsWith('s')) s = s.slice(0, -1);
  if (s.length > 1 && s.endsWith('e')) s = s.slice(0, -1);
  if (s.length > 1 && s.endsWith('i')) s = `${s.slice(0, -1)}y`;

  return s;
}

export function findNearDuplicates(categories: Category[]): Category[][] {
  const groups = new Map<string, Category[]>();
  for (const category of categories) {
    const key = `${category.kind}:${normaliseForComparison(category.name)}`;
    groups.set(key, [...(groups.get(key) ?? []), category]);
  }
  return [...groups.values()].filter((group) => group.length > 1);
}
